import { useState } from 'react'
import type { SupplierMitigationStatus } from '../../types'
import {
  DIMENSION_KEYS,
  scoreBandWord,
  scoreValue,
  type RiskRatingSupplier,
} from './supplierRiskRatingsTable'
import type { ProfileMitigation } from './tryAnswerRiskRatingsFromDb'
import { CopyTableButtons } from './CopyTableButtons'
import { EsBadge, riskBadgeTone } from './ui'
import { riskBand, supplierDisplayName } from './supplierRollup'

function heatmapCellClass(value: number | null): string {
  if (value == null) return 'bg-es-haze text-es-muted'
  const b = riskBand(value)
  if (b === 'high') return 'bg-red-100 text-es-riskHigh'
  if (b === 'medium') return 'bg-amber-100 text-es-riskMedium'
  return 'bg-emerald-50 text-es-riskLow'
}

/** Score-only overall band (same as Intelligence view). */
function overallBand(s: RiskRatingSupplier): 'high' | 'medium' | 'low' | null {
  if (s.overall_score != null && Number.isFinite(Number(s.overall_score))) {
    return riskBand(Number(s.overall_score))
  }
  return null
}

function formatUpdated(iso: string | null | undefined): string | null {
  if (!iso) return null
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return null
  }
}

function normalizeMitigationStatus(status: SupplierMitigationStatus): 'open' | 'on_hold' | 'closed' {
  if (status === 'closed' || status === 'completed') return 'closed'
  if (status === 'on_hold' || status === 'scheduled') return 'on_hold'
  return 'open'
}

function mitigationStatusLabel(status: SupplierMitigationStatus): string {
  const n = normalizeMitigationStatus(status)
  if (n === 'on_hold') return 'On-hold'
  if (n === 'closed') return 'Closed'
  return 'Open'
}

function mitigationStatusTone(status: SupplierMitigationStatus): 'quiet' | 'riskMedium' | 'default' {
  const n = normalizeMitigationStatus(status)
  if (n === 'closed') return 'quiet'
  if (n === 'on_hold') return 'riskMedium'
  return 'default'
}

function profileCopyPayload(
  supplier: RiskRatingSupplier,
  brands: string[],
  mitigations: ProfileMitigation[]
) {
  const band = overallBand(supplier)
  const meta = [supplier.location, supplier.kenvue_region, supplier.category].filter(Boolean).join(' · ')
  const overall =
    supplier.overall_score != null && Number.isFinite(Number(supplier.overall_score))
      ? String(Number(supplier.overall_score))
      : null
  const details: string[] = []
  if (supplier.single_source === true) details.push('Single-source')
  else if (supplier.single_source === false) details.push('Multi-source')
  if (supplier.supplier_unique_id) details.push(`ID: ${supplier.supplier_unique_id}`)
  const updated = formatUpdated(supplier.updated_at)
  if (updated) details.push(`Updated ${updated}`)

  const headers = ['Parameter', 'Rating (/10)', 'Band', 'Commentary']
  const rows = DIMENSION_KEYS.map(({ key, label }) => {
    const v = scoreValue(supplier, key)
    const b = v != null ? scoreBandWord(v) : '—'
    const narr = (supplier.risk_dimension_narratives?.[key] ?? '').trim()
    return [label, v != null ? String(v) : '—', b, narr || '—']
  })

  const mitigationLines =
    mitigations.length === 0
      ? ['Mitigations: none']
      : [
          'Mitigations:',
          ...mitigations.map((m) => {
            const status = mitigationStatusLabel(m.status)
            const brand = m.brand && m.brand !== '—' ? m.brand : null
            return [status, m.date, m.task, brand].filter(Boolean).join(' · ')
          }),
        ]

  const title = supplierDisplayName(supplier) || 'Supplier'
  const preamble = [
    `${title}${band ? ` · ${band.toUpperCase()} RISK` : ''}`,
    meta,
    details.join(' · '),
    overall != null ? `Overall: ${overall} / 10${supplier.risk_gauge ? ` · ${supplier.risk_gauge}` : ''}` : '',
    brands.length > 0 ? `Brands: ${brands.join(', ')}` : 'Brands: —',
    ...mitigationLines,
  ].filter(Boolean)

  return { headers, rows, preamble }
}

type Props = {
  supplier: RiskRatingSupplier
  /** Distinct brand names for this legal supplier (A→Z). */
  brands?: string[]
  mitigations?: ProfileMitigation[]
}

/** Full supplier profile + Intelligence view–style risk matrix for Risk analyst chat. */
export function SupplierRiskHeatmapChatCard({
  supplier,
  brands: brandsProp,
  mitigations = [],
}: Props) {
  const [expandedDim, setExpandedDim] = useState<string | null>(null)
  const brands =
    brandsProp && brandsProp.length > 0
      ? brandsProp
      : supplier.brand?.trim()
        ? [supplier.brand.trim()]
        : []
  const band = overallBand(supplier)
  const title = supplierDisplayName(supplier) || 'Supplier'
  const meta = [supplier.location, supplier.kenvue_region, supplier.category].filter(Boolean).join(' · ')
  const overall =
    supplier.overall_score != null && Number.isFinite(Number(supplier.overall_score))
      ? String(Number(supplier.overall_score))
      : null
  const parentLabel = (supplier.parent_name || '').trim()
  const showParent = !!parentLabel && parentLabel.toLowerCase() !== title.toLowerCase()
  const entityNames = (supplier.product_line_names || [])
    .map((n) => n.trim())
    .filter((n) => n && n.toLowerCase() !== title.toLowerCase())
  const details: string[] = []
  if (supplier.procurement_type === 'indirect') details.push('Indirect')
  else if (supplier.procurement_type === 'direct') details.push('Direct')
  if (showParent) details.push(`Parent: ${parentLabel}`)
  if (entityNames.length) details.push(`Entities: ${entityNames.join(', ')}`)
  if (supplier.single_source === true) details.push('Single-source')
  else if (supplier.single_source === false) details.push('Multi-source')
  if (supplier.supplier_unique_id) details.push(`ID: ${supplier.supplier_unique_id}`)
  const updated = formatUpdated(supplier.updated_at)
  if (updated) details.push(`Updated ${updated}`)

  const copyPayload = profileCopyPayload(supplier, brands, mitigations)
  const hasAnyCommentary = DIMENSION_KEYS.some(
    ({ key }) => (supplier.risk_dimension_narratives?.[key] ?? '').trim().length > 0
  )

  return (
    <div className="space-y-3 min-w-[260px] max-w-xl">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-bold text-es-deep leading-tight">{title}</h3>
            {band && (
              <EsBadge tone={riskBadgeTone(band)}>
                {band === 'high' ? 'HIGH RISK' : band === 'medium' ? 'MEDIUM RISK' : 'LOW RISK'}
              </EsBadge>
            )}
          </div>
          {meta && <p className="text-xs text-es-muted mt-0.5">{meta}</p>}
          {details.length > 0 && (
            <p className="text-[11px] text-es-muted mt-0.5">{details.join(' · ')}</p>
          )}
        </div>
        <div className="text-right shrink-0">
          {overall != null && (
            <div
              className={`text-2xl font-bold tabular-nums leading-none ${
                band === 'high'
                  ? 'text-es-riskHigh'
                  : band === 'medium'
                    ? 'text-es-riskMedium'
                    : 'text-es-riskLow'
              }`}
            >
              {overall} <span className="text-sm font-semibold text-es-muted">/ 10</span>
            </div>
          )}
          {supplier.risk_gauge && (
            <p className="text-[11px] text-es-muted mt-0.5">{supplier.risk_gauge}</p>
          )}
        </div>
      </div>

      <div>
        <h4 className="text-xs font-bold text-es-muted uppercase tracking-wider mb-1.5">Brands</h4>
        {brands.length === 0 ? (
          <p className="text-sm text-es-muted">—</p>
        ) : (
          <ul className="flex flex-wrap gap-1.5">
            {brands.map((b) => (
              <li
                key={b}
                className="text-xs font-medium text-es-navy bg-es-haze border border-es-soft rounded px-2 py-0.5"
              >
                {b}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <h4 className="text-xs font-bold text-es-muted uppercase tracking-wider mb-1.5">
          Mitigations
        </h4>
        {mitigations.length === 0 ? (
          <p className="text-sm text-es-muted">No mitigations in Tracker for this supplier.</p>
        ) : (
          <ul className="space-y-2">
            {mitigations.map((m, i) => (
              <li
                key={`${m.task}-${m.date}-${i}`}
                className="rounded-[var(--radius-sm)] border border-es-soft bg-es-white px-2.5 py-2"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <p className="text-sm text-es-body leading-snug min-w-0 flex-1">{m.task}</p>
                  <EsBadge
                    tone={mitigationStatusTone(m.status)}
                    className="!text-[10px] !px-1.5 !py-0.5 shrink-0"
                  >
                    {mitigationStatusLabel(m.status)}
                  </EsBadge>
                </div>
                <p className="text-[11px] text-es-muted mt-1">
                  {[m.date, m.location || null, m.brand !== '—' ? m.brand : null]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h4 className="text-xs font-bold text-es-muted uppercase tracking-wider">
          Risk Heatmap (14 Categories)
        </h4>
        <CopyTableButtons payload={copyPayload} />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {DIMENSION_KEYS.map(({ key, label }) => {
          const v = scoreValue(supplier, key)
          const narrative = (supplier.risk_dimension_narratives?.[key] ?? '').trim()
          const open = expandedDim === key
          return (
            <div key={key} className="min-w-0">
              <button
                type="button"
                disabled={!narrative}
                onClick={() => setExpandedDim(open ? null : key)}
                className={`w-full rounded-[var(--radius-md)] border border-es-soft p-2 text-center ${heatmapCellClass(v)} ${
                  narrative ? 'cursor-pointer hover:ring-1 hover:ring-es-signal/40' : 'cursor-default'
                } ${open ? 'ring-1 ring-es-signal/50' : ''}`}
                title={narrative ? 'Show commentary' : undefined}
              >
                <div className="text-lg font-bold tabular-nums">{v != null ? v : '–'}</div>
                <div className="text-[10px] font-medium leading-tight mt-0.5 opacity-80">
                  {label}
                  {narrative ? (
                    <i
                      className={`fa-solid fa-chevron-${open ? 'up' : 'down'} ml-1 text-[9px] opacity-70`}
                      aria-hidden
                    />
                  ) : null}
                </div>
              </button>
            </div>
          )
        })}
      </div>

      <div>
        <h4 className="text-xs font-bold text-es-muted uppercase tracking-wider mb-1.5">
          Risk Commentary
        </h4>
        {hasAnyCommentary ? (
          <ul className="divide-y divide-es-soft border border-es-soft rounded-[var(--radius-md)] overflow-hidden">
            {DIMENSION_KEYS.map(({ key, label }) => {
              const v = scoreValue(supplier, key)
              const narrative = (supplier.risk_dimension_narratives?.[key] ?? '').trim()
              const open = expandedDim === key
              return (
                <li key={`narr-${key}`}>
                  <button
                    type="button"
                    className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-es-haze disabled:opacity-60 disabled:hover:bg-transparent"
                    onClick={() => setExpandedDim(open ? null : key)}
                    disabled={!narrative}
                  >
                    <span className="text-sm text-es-body">{label}</span>
                    <span className="text-sm tabular-nums text-es-muted shrink-0">
                      {v != null ? `${v}/10` : '–'}
                      {narrative ? (
                        <i
                          className={`fa-solid fa-chevron-${open ? 'up' : 'down'} ml-2 text-es-muted text-xs`}
                          aria-hidden
                        />
                      ) : null}
                    </span>
                  </button>
                  {open && narrative ? (
                    <div className="px-3 pb-3 text-sm text-es-body whitespace-pre-wrap border-t border-es-soft bg-es-haze/50">
                      {narrative}
                    </div>
                  ) : null}
                </li>
              )
            })}
          </ul>
        ) : (
          <p className="text-sm text-es-muted">No commentaries in platform data for this supplier.</p>
        )}
      </div>

      <p className="text-[11px] text-es-muted">
        Source: Intelligence view + Mitigation tracker.
        {hasAnyCommentary ? ' Expand a parameter under Risk Commentary to read the narrative. ' : ' '}
        Copy includes profile, brands, mitigations, ratings, and commentaries when present.
      </p>
    </div>
  )
}
